import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import Separator from 'flarum/common/components/Separator';
import ItemList from 'flarum/common/utils/ItemList';
import extractText from 'flarum/common/utils/extractText';
import Picture from '../models/Picture';
import openEditorModal from './openEditorModal';
import StickersModal from '../modals/StickersModal';

const translationPrefix = 'kilowhat-cimaise.forum.picture-controls.';

let approving = false;
let favoriting = false;
let hiding = false;
let restoring = false;
let deleting = false;

export interface PictureThumbnailContext {
    ondelete?: () => void
}

export default {
    controls(picture: Picture, context: PictureThumbnailContext = {}) {
        const items = new ItemList();

        ['user', 'moderation', 'destructive'].forEach((section) => {
            const controls = ((this as any)[section + 'Controls'](picture, context) as ItemList<any>).toArray();
            if (controls.length) {
                controls.forEach(item => items.add(item.itemName, item));
                items.add(section + 'Separator', Separator.component());
            }
        });

        return items;
    },

    userControls(picture: Picture, context: PictureThumbnailContext): ItemList<any> {
        const items = new ItemList();

        if (picture.canEditImage()) {
            items.add('editor', Button.component({
                icon: 'fas fa-crop',
                onclick() {
                    openEditorModal(StickersModal, {
                        picture,
                    });
                },
            }, app.translator.trans(translationPrefix + 'editor')));
        }

        return items;
    },

    moderationControls(picture: Picture, context: PictureThumbnailContext): ItemList<any> {
        const items = new ItemList();

        if (picture.canApprove() && !picture.isApproved()) {
            items.add('approve', Button.component({
                icon: 'fas fa-check',
                loading: approving,
                onclick: () => {
                    approving = true;

                    picture.save({
                        isApproved: true,
                    }).then(() => {
                        approving = false;
                        m.redraw();
                    }).catch(err => {
                        approving = false;
                        m.redraw();
                        throw err;
                    });
                },
            }, app.translator.trans(translationPrefix + 'approve')));
        }

        if (picture.canFavorite()) {
            items.add('favorite', Button.component({
                icon: 'fas fa-star',
                loading: favoriting,
                onclick: () => {
                    favoriting = true;

                    picture.save({
                        isFavorite: !picture.isFavorite(),
                    }).then(() => {
                        favoriting = false;
                        m.redraw();
                    }).catch(err => {
                        favoriting = false;
                        m.redraw();
                        throw err;
                    });
                },
            }, app.translator.trans(translationPrefix + (picture.isFavorite() ? 'remove-favorite' : 'set-favorite'))));
        }

        return items;
    },

    destructiveControls(picture: Picture, context: PictureThumbnailContext): ItemList<any> {
        const items = new ItemList();

        if (picture.isHidden() && picture.canRestore()) {
            items.add('restore', Button.component({
                icon: 'fas fa-reply',
                loading: restoring,
                onclick: () => {
                    restoring = true;

                    picture.save({
                        isHidden: false,
                    }).then(() => {
                        restoring = false;
                        m.redraw();
                    }).catch(err => {
                        restoring = false;
                        m.redraw();
                        throw err;
                    });
                },
            }, app.translator.trans(translationPrefix + 'restore')));
        }

        if (!picture.isHidden() && picture.canHide()) {
            items.add('hide', Button.component({
                icon: 'far fa-trash-alt',
                loading: hiding,
                onclick: () => {
                    hiding = true;

                    picture.save({
                        isHidden: true,
                    }).then(() => {
                        hiding = false;
                        m.redraw();
                    }).catch(err => {
                        hiding = false;
                        m.redraw();
                        throw err;
                    });
                },
            }, app.translator.trans(translationPrefix + 'hide')));
        }

        if (picture.canDelete() && picture.isHidden()) {
            items.add('delete', Button.component({
                icon: 'fas fa-times',
                loading: deleting,
                onclick: () => {
                    if (confirm(extractText(app.translator.trans(translationPrefix + 'delete-confirmation', {
                        title: picture.title(),
                    })))) {
                        deleting = true;

                        picture.delete().then(() => {
                            deleting = false;

                            context.ondelete?.();
                            m.redraw();
                        }).catch(err => {
                            deleting = false;
                            m.redraw();
                            throw err;
                        });
                    }
                },
            }, app.translator.trans(translationPrefix + 'delete')));
        }

        return items;
    },
}
